import { ReactElement, useEffect, useState, useRef } from "react";
import ReCAPTCHA from 'react-google-recaptcha'
import useApiState, { api } from "../../hooks/use-api-state";
import { IAppInfo, IUserInfo } from "../../messages/IAppInfo";
import Loading from "../common/Loading";
import styles from "./SelfAssessment.module.css";
import "../common/General.module.css";
import Assessment from "./Assessment";
import { IAssessmentObject, IQuestion, IAssessmentResponse, IAnswersData } from "../../messages/IAssessments";

export default function SelfAssessment(props: {
  appInfo: IAppInfo,
  userInfo: IUserInfo,
}): ReactElement {
  const [getAssessmentApiState, setGetAssessmentApiState] = useApiState(false);
  const [assessment, setAssessment] = useState<IAssessmentResponse | null>(null);
  const [assessmentObject, setAssessmentObject] = useState<IAssessmentObject | null>(null);
  const [questions, setQuestions] = useState<IQuestion[] | null>(null);
  const [answersData, setAnswersData] = useState<IAnswersData | null>(null);
  const [email, setEmail] = useState<string | null>(null);
  const [fullname, setFullname] = useState<string | null>(null);
  //
  const [message, setMessage] = useState<string | null>(null);
  const [showOnly, setShowOnly] = useState(false);
  const [recaptchaOK, setRecaptchaOK] = useState(false);
  const recaptcha = useRef<ReCAPTCHA | null>(null);

  
  useEffect(() => {
    document.title = "Cydrill self assessment";
    const searchParams = new URLSearchParams(window.location.search);
    const cohort_id = searchParams.get("cohort_id") || "Anonymous";
    const lmodule_code = searchParams.get("lmodule_code") || "-";
    const questions_id = searchParams.get("id") || "default";
    const user_id = searchParams.get("user_id") || null;
    if (user_id)
      setShowOnly(true);
    getAssessment(cohort_id, lmodule_code, questions_id, user_id);
  }, [props.userInfo, props.appInfo]);


  useEffect(() => {
    if (assessment?.assessment_obj)
      setAssessmentObject(assessment?.assessment_obj);
    setQuestions(assessment?.questions ?? null);
    setAnswersData(assessment?.answers_data ?? null);
  }, [assessment]);


  async function getAssessment(cohort_id: string, lmodule_code: string, questions_id: string, user_id:string|null) {
      try {
        let ret = await api.getAssessment(setGetAssessmentApiState, true, cohort_id, lmodule_code, questions_id, user_id);
        //console.log(`questions=${JSON.stringify(ret.questions)}`);
        setAssessment(ret);
        if (ret.assessment_obj?.email)
          setEmail(ret.assessment_obj?.email);
        if (ret.assessment_obj?.fullname)
          setFullname(ret.assessment_obj?.fullname);
        setMessage(ret.message??null);
      } catch (e: any) {
        console.error("Error fetching assessment:", e);
      }
  }


  function updateAssessment(submitting:boolean, assessment_obj: IAssessmentObject, answers_data: IAnswersData|null) {
    if (submitting) { // Just indicating that the submission has started
      if (assessment_obj) {
        assessment_obj.email = email;
        assessment_obj.fullname = fullname;
      }
    }
    else {
      setAnswersData(answers_data);
      setAssessmentObject(assessment_obj);
    }
  }

  function recaptchaChanged(token: string|null) {
      setRecaptchaOK(!!recaptcha.current?.getValue());
  };

  //console.log(`readonly=${showOnly || !recaptchaOK || !fullname || !email}`);

  return (
    <>
      <Loading isLoading={getAssessmentApiState.isLoading}>
        {assessmentObject && questions ? (
          <>
            {message && (
              <div className={styles.message}>
                {message}
              </div>
            )}
            {!showOnly && (
              <form className={styles.userInputForm}>
                  <div className={styles.inputField}>
                    <label>
                    Name:
                      <input
                        type="text"
                        name="name"
                        value={fullname ?? ""}
                        placeholder="Your name"
                        onChange={e => setFullname(e.target.value)}
                        disabled={getAssessmentApiState.isLoading || showOnly || answersData?.submitted}
                      />
                    </label>
                    {!(!!fullname && !!email) && (
                      <span className={styles.remark}>Provide your name and email address to start the assessment.</span>
                    )}
                  </div>
                  <div className={styles.inputField}>
                    <label>
                      Email:
                      <input
                        type="email"
                        name="email"
                        value={email ?? ""}
                        placeholder="example@domain.com"
                        onChange={e => setEmail(e.target.value)}
                        disabled={getAssessmentApiState.isLoading || showOnly || answersData?.submitted}
                        style={{ marginLeft: "0.5em" }}
                      />
                    </label>
                    <span className={styles.remark}>We'll send you the results by email to this address.</span>
                  </div>
                  {/*!answersData?.submitted && (
                    <div className={styles.recaptcha}>
                      <ReCAPTCHA ref={recaptcha} sitekey={props.appInfo.captcha_sitekey} onChange={recaptchaChanged}/>
                    </div>
                  )*/}
              </form>
            )}
            <Assessment
              anonymous={true}
              appInfo={props.appInfo}
              userInfo={props.userInfo}
              assessmentObject={assessmentObject}
              questions={questions}
              answersData={answersData}
              showOnly={showOnly}
              allowFilling={!showOnly && !!fullname && !!email}
              allowSubmit={recaptchaOK && !!fullname && !!email}
              canSave={false}
              recaptchaToken={recaptcha.current?.getValue() ?? null}
              setMessage={setMessage}
              updateAssessment={updateAssessment}
            />
            <div className={styles.bottomRecaptchaDiv}>
              {!answersData?.submitted && (
                <div className={styles.recaptcha}>
                  <ReCAPTCHA ref={recaptcha} sitekey={props.appInfo.captcha_sitekey} onChange={recaptchaChanged}/>
                </div>
              )}
            </div>
          </>
          ) : 
          (
            <></>
          )}
      </Loading>
    </>
  );
}
